/**
 * Client device detection from the user agent (OS family + form factor).
 */

/** Operating system family reported by the browser. */
export type DeviceOs = 'macos' | 'ios' | 'windows' | 'linux' | 'android' | 'chromeos' | 'unknown';

/** Form factor inferred from the user agent. */
export type DeviceForm = 'desktop' | 'mobile' | 'tablet' | 'unknown';

/** Snapshot of the client device for UI that depends on the platform. */
export interface DeviceInfo {
  os: DeviceOs;
  form: DeviceForm;
  /** True when shortcuts should show ⌘ instead of Ctrl (Apple keyboards). */
  usesCommandKey: boolean;
}

/** Parse `navigator.userAgent` into OS family, form factor, and shortcut modifier. */
export function detectDevice(): DeviceInfo {
  const userAgent = navigator.userAgent;

  if (/iPhone|iPod/.test(userAgent)) {
    return { os: 'ios', form: 'mobile', usesCommandKey: true };
  }
  if (/iPad/.test(userAgent) || (isMacintoshUa(userAgent) && navigator.maxTouchPoints > 1)) {
    return { os: 'ios', form: 'tablet', usesCommandKey: true };
  }
  if (isMacintoshUa(userAgent)) {
    return { os: 'macos', form: 'desktop', usesCommandKey: true };
  }
  if (/Android/.test(userAgent)) {
    return {
      os: 'android',
      form: /Mobile/.test(userAgent) ? 'mobile' : 'tablet',
      usesCommandKey: false,
    };
  }
  if (/CrOS/.test(userAgent)) {
    return { os: 'chromeos', form: 'desktop', usesCommandKey: false };
  }
  if (/Windows/.test(userAgent)) {
    return { os: 'windows', form: 'desktop', usesCommandKey: false };
  }
  if (/Linux/.test(userAgent)) {
    return { os: 'linux', form: 'desktop', usesCommandKey: false };
  }

  return { os: 'unknown', form: 'unknown', usesCommandKey: false };
}

/** True when the user agent string is a Mac desktop (not iPadOS pretending to be Mac). */
function isMacintoshUa(userAgent: string): boolean {
  return /Macintosh|Mac OS X/.test(userAgent);
}
